import { Button, Col, Form, Modal, notification, Row, Space, Table } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { FC, useMemo, useState } from 'react'
import Title from 'antd/es/typography/Title'
import Supplier from './components/form-items/supplier/supplier'
import Width from './components/form-items/width/width'
import Quantity from './components/form-items/quantity/quantity'
import { SaveOutlined } from '@ant-design/icons'
import FormValues from './types/form-values'
import MaterialTypes, {
    query,
} from '../../../../../components/form-items/material-types/material-types'
import { useGetMaterialTypesQuery } from '../../../../../store/api/material-types-api'
import {
    useCreateMaterialMutation,
    useLazyGetMaterialsQuery,
} from '../../../../../store/api/materials-api'
import { StrapiCreate } from '../../../../../types/strapi/strapi'
import Material from '../../../../../types/api/material'
import { useCreateStockMutation } from '../../../../../store/api/stocks-api'
import getColumns from './constants/columns'

export interface ReceiptPosition {
    materialType: { documentId: string; name: string }
    width: number
    quantity: number
    unit: string
}

interface ReceiptFormProps {
    onSuccess: () => void
}

const ReceiptForm: FC<ReceiptFormProps> = ({ onSuccess }) => {
    const [form] = useForm<FormValues>()
    const [list, setList] = useState<ReceiptPosition[]>([])
    const { data } = useGetMaterialTypesQuery(query)
    const [getMaterials, getMaterialsState] = useLazyGetMaterialsQuery()
    // const [createPosition] = useCreateReceiptPositionMutation()
    // const [createReceiptMutation] = useCreateReceiptMutation()
    const [createMaterial, createMaterialState] = useCreateMaterialMutation()
    const [createStock, createStockState] = useCreateStockMutation()
    const isLoading =
        getMaterialsState.isLoading ||
        createMaterialState.isLoading ||
        createStockState.isLoading

    const createMaterialByPosition = async (position: ReceiptPosition) => {
        const data: StrapiCreate<Material> = {
            data: {
                width: position.width,
                material_type: { set: [position.materialType.documentId] },
            },
        }
        const res = await createMaterial(data)
        if ('data' in res && res.data) return res.data.data
        else return null
    }

    const getMaterialByPosition = async (position: ReceiptPosition) => {
        return await getMaterials({
            filters: {
                material_type: { documentId: { $eq: position.materialType.documentId } },
                width: { $eq: position.width },
            },
            populate: '*',
        })
    }

    const createPositions = async (): Promise<string[]> => {
        const docIdList: string[] = []

        for (let i = 0; i < list.length; i++) {
            const position = list[i]
            const res = await getMaterialByPosition(position)

            if (!res.data) return [] // TODO: Тут подумать

            let material: Material | null = null

            if (res.data?.data.length === 0) {
                // если такого материала нету
                material = await createMaterialByPosition(position)
            } else {
                // если такой материал уже есть
                material = res.data.data[0]
            }

            if (!material) {
                console.error('Нет материала')
                return []
            }

            // const creatingRes = await createPosition({
            //     data: {
            //         material: { set: [material.documentId] },
            //         length: getLengthInKmUnit(position),
            //     },
            // })

            // if ('data' in creatingRes && creatingRes.data) {
            //     docIdList.push(creatingRes.data.data.documentId)
            // }
        }

        return docIdList
    }

    const createReceipt = async (receiptPositionsDocIds: string[]) => {
        // const values = form.getFieldsValue()
        // const res = await createReceiptMutation({
        //     data: {
        //         supplier: { set: [values.supplier] },
        //         receiptStatus: 'Завершен',
        //         receipt_positions: { set: receiptPositionsDocIds },
        //     },
        // })
    }

    const getLengthInKmUnit = (position: ReceiptPosition) => {
        if (position.unit === 'km') {
            return position.quantity
        } else {
            const widthInKm = position.width / 100000
            const lengthInKm = position.quantity / widthInKm
            return lengthInKm
        }
    }

    const createStocks = async () => {
        for (let i = 0; i < list.length; i++) {
            const position = list[i]
            const getRes = await getMaterialByPosition(position)
            const material = getRes.data?.data[0]!

            // TODO: надо бы не создавать каждый раз новый, а обновлять остаток, но это должно быть в транзакции
            await createStock({
                data: {
                    material: { set: [material.documentId] },
                    amount: getLengthInKmUnit(position),
                    locked: false,
                },
            })
        }
    }

    const onAddPosition = async () => {
        try {
            await form.validateFields(['materialTypes', 'width', 'quantity'])
            const values = form.getFieldsValue()

            const item = {
                key: values.materialTypes + values.width,
                materialType: data?.data.find(item => item.documentId === values.materialTypes)!,
                width: values.width,
                quantity: values.quantity,
                unit: values.unit,
            }

            setList(prevList => [...prevList, item])

            form.setFieldsValue({
                width: undefined,
                quantity: undefined,
            })

            form.getFieldInstance('width').focus()
        } catch (e) {
            console.error(e)
        }
    }

    const onRemovePosition = (index: number) => {
        setList(prevList => prevList.filter((_, i) => i !== index))
    }

    const clearFormAndList = () => {
        form.resetFields()
        setList([])
    }

    const validateList = (): boolean => {
        if (list.length === 0) {
            Modal.error({ title: 'Ошибка валидации', content: 'Недобавлено ни одной позиции' })
            return false
        }

        return true
    }

    const onSaveReceipt = async () => {
        try {
            await form.validateFields(['supplier'])
            const isCorrect = validateList()

            if (isCorrect) {
                const positionsRes = await createPositions()
                await createReceipt(positionsRes!)
                await createStocks()

                notification.success({ message: 'Приход материала успешно сохранен' })
                clearFormAndList()
                onSuccess()
            }
        } catch (e) {
            console.error(e)
        }
    }

    const columns = useMemo(() => {
        return getColumns(onRemovePosition)
    }, [])

    return (
        <Form form={form} layout={'vertical'} initialValues={{ unit: 'km', rolls: 1 }}>
            <Title style={{ marginTop: 0 }} level={2}>
                Прием материала
            </Title>
            <Row gutter={18}>
                <Col sm={6}>
                    <Supplier />
                </Col>
                <Col sm={18}></Col>
                <Col sm={10}>
                    <MaterialTypes />
                </Col>
                <Col sm={2}>
                    <Width />
                </Col>
                <Col sm={4}>
                    <Quantity />
                </Col>
                <Col sm={4}>
                    <Form.Item label={' '}>
                        <Button type={'primary'} onClick={onAddPosition}>
                            Добавить позицию
                        </Button>
                    </Form.Item>
                </Col>
            </Row>
            <Table
                size={'small'}
                dataSource={list}
                rowKey={item => item.materialType.documentId + item.width}
                columns={columns}
                pagination={false}
            />
            <Space size={18} style={{ marginTop: 24 }}>
                <Button onClick={() => clearFormAndList()}>Очистить</Button>
                <Button
                    type={'primary'}
                    icon={<SaveOutlined />}
                    loading={isLoading}
                    onClick={onSaveReceipt}
                >
                    Сохранить приход
                </Button>
            </Space>
        </Form>
    )
}

export default ReceiptForm
